import React from "react";
import ReactApexChart from "react-apexcharts";

const NAVY = "#002E70";
const OPEN = "#0f6cbd";
const PROGRESS = "#f8b425";
const RESOLVED = "#9BD732";

const FONT = "'Montserrat', sans-serif";

/* Ticket counts are whole numbers, so the axis has to step in whole numbers.
   Left to itself Apex picks fractional ticks for small maxima (0, 0.5, 1 ...)
   and rounding those to integers repeats labels - so the top of the axis is
   rounded up to a whole multiple of a whole step, at most five steps tall. */
const MAX_Y_STEPS = 5;

const integerAxis = (values) => {
    const peak = Math.max(1, ...values.map((value) => Number(value) || 0));
    const step = Math.ceil(peak / MAX_Y_STEPS);
    const steps = Math.ceil(peak / step);
    return { max: steps * step, tickAmount: steps };
};

export const TicketsBarChart = ({ data = [], height = 240 }) => {
    const isAllZero =
        data.length === 0 || data.every((row) => !Number(row.value));

    if (isAllZero) {
        return (
            <div className="vp-chart-empty" style={{ height }}>
                No new tickets this week
            </div>
        );
    }

    const axis = integerAxis(data.map((row) => row.value));
    const options = {
        chart: {
            type: "bar",
            fontFamily: FONT,
            toolbar: { show: false },
            animations: { enabled: true, easing: "easeout", speed: 550 },
            parentHeightOffset: 0,
        },
        plotOptions: {
            bar: {
                columnWidth: "45%",
                borderRadius: 6,
                borderRadiusApplication: "end",
                dataLabels: { position: "top" },
            },
        },
        colors: [NAVY],
        dataLabels: {
            enabled: true,
            offsetY: -18,
            style: { fontSize: "11px", fontWeight: 600, colors: ["#33415c"] },
        },
        grid: {
            borderColor: "#e9edf3",
            strokeDashArray: 4,
            padding: { left: 4, right: 4, top: 0 },
        },
        xaxis: {
            categories: data.map((row) => row.day),
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { style: { fontSize: "12px", colors: "#6c757d" } },
        },
        yaxis: {
            min: 0,
            max: axis.max,
            tickAmount: axis.tickAmount,
            forceNiceScale: false,
            decimalsInFloat: 0,
            labels: {
                style: { fontSize: "12px", colors: "#6c757d" },
                formatter: (value) =>
                    Number.isInteger(value) ? String(value) : String(Math.round(value)),
            },
        },
        tooltip: {
            y: { formatter: (value) => `${value} tickets` },
        },
        states: { hover: { filter: { type: "darken", value: 0.88 } } },
    };

    return (
        <ReactApexChart
            type="bar"
            height={height}
            series={[{ name: "Tickets", data: data.map((row) => row.value) }]}
            options={options}
        />
    );
};

const DONUT_COLORS = [OPEN, PROGRESS, RESOLVED];

/* Fixed categorical order - slot 1 is always slot 1, so a category keeps its
   colour even when the ranking below it changes. Validated for colour-vision
   deficiency against a light surface; the slice labels and the legend counts are
   the required relief for the three lighter hues. */
export const CATEGORY_COLORS = [
    "#2a78d6",
    "#eb6834",
    "#1baf7a",
    "#eda100",
    "#e87ba4",
    "#008300",
    "#4a3aa7",
];

export const TicketStatusDonut = ({
    data = [],
    height = 250,
    total,
    totalLabel = "Total",
}) => {
    const centreTotal =
        total === undefined || total === null
            ? data.reduce((sum, row) => sum + Number(row.value || 0), 0)
            : Number(total);

    const options = {
        chart: { type: "donut", fontFamily: FONT },
        labels: data.map((row) => row.label),
        colors: data.map(
            (row, index) => row.color || DONUT_COLORS[index % DONUT_COLORS.length]
        ),
        stroke: { width: 0 },
        legend: { show: false },
        dataLabels: {
            enabled: true,
            formatter: (percent) => `${Math.round(percent)}%`,
            style: { fontSize: "11px", fontWeight: 600 },
            dropShadow: { enabled: false },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "68%",
                    labels: {
                        show: true,
                        value: {
                            fontSize: "22px",
                            fontWeight: 700,
                            color: NAVY,
                            offsetY: 2,
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: totalLabel,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#6c757d",
                            formatter: () => String(centreTotal),
                        },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (value) => `${value} tickets` } },
    };

    return (
        <ReactApexChart
            type="donut"
            height={height}
            series={data.map((row) => Number(row.value || 0))}
            options={options}
        />
    );
};
